import { createElement } from "react";

import { cssVariableForColor, cssVariableForMargin } from "../../theme/cssVariableHelpers";
import { mergeClassNames } from "../../utils/classes";
import { Badge } from "../Badge/Badge";
import { TypoGraphicElementInternals } from "./TypographicElements.internals";
import { marginAwayToClassNameMapping, textStylesToClassNameMapping } from "./TypographicElements.styles";

import type { BadgeProps } from "../Badge/Badge";
import type {
	ContainerProps,
	HeadingLevelSupportProps,
	InternalTypographicSectionProps,
	MarginProps,
	TextBlockAlignProps,
	TextStyleProps,
} from "./TypographicElements.types";
import type { FC, PropsWithChildren, ReactNode } from "react";

import * as styles from "./typographicElements.module.scss";

const createSectionElement: FC<PropsWithChildren<InternalTypographicSectionProps>> = ({
	type = "section",
	bumpHeadingLevel,
	resetHeadingLevelTo,
	children,
	marginAway,
	textCenter,
	style: textStyle,
	color,
	testId,
}) => {
	const section = createElement(
		type,
		{
			style: cssVariableForColor(color, "--typographic-element-color-prop-value"),
			className: mergeClassNames(
				styles.sectionWrapper,
				marginAway && marginAwayToClassNameMapping[marginAway],
				type === "p" && styles.whiteSpacePreLine,
				textCenter && styles.textCenter,
				textStyle && textStylesToClassNameMapping[textStyle],
				color && styles.useVariableColor,
			),
			"data-testid": testId,
		},
		children,
	);

	if (bumpHeadingLevel || resetHeadingLevelTo) {
		return (
			<TypoGraphicElementInternals.HeadingLevel resetHeadingLevelTo={resetHeadingLevelTo}>
				{section}
			</TypoGraphicElementInternals.HeadingLevel>
		);
	}

	return section;
};

type SectionProps = MarginProps &
	HeadingLevelSupportProps &
	ContainerProps &
	TextBlockAlignProps &
	TextStyleProps & { testId?: string };

export const Section: FC<PropsWithChildren<SectionProps>> = props => {
	return createSectionElement({
		bumpHeadingLevel: props.bumpHeadingLevel ?? true,
		...props,
		type: "section",
	});
};

/** @deprecated Prefer using Div instead. */
export const SectionDiv: FC<PropsWithChildren<SectionProps>> = props => {
	return createSectionElement({ ...props, type: "div" });
};

export const Div = SectionDiv;

type ArticleProps = HeadingLevelSupportProps & { testId?: string };

export const Article: FC<PropsWithChildren<ArticleProps>> = props => {
	return createSectionElement({
		bumpHeadingLevel: props.bumpHeadingLevel ?? true,
		...props,
		type: "article",
	});
};

type ParagraphProps = MarginProps & TextBlockAlignProps & TextStyleProps & ContainerProps;

export const Paragraph: FC<PropsWithChildren<ParagraphProps>> = props => {
	return createSectionElement({ ...props, type: "p" });
};

/** @deprecated Use TextSpan with a bold style instead. */
export const Strong: FC<PropsWithChildren> = props => {
	return <strong className={styles.strong}>{props.children}</strong>;
};

export const Caption: FC<PropsWithChildren> = props => {
	return <span className={styles.fontCaptionDefault}>{props.children}</span>;
};

type TextSpanProps = { testId?: string } & TextBlockAlignProps & TextStyleProps;

export const TextSpan: FC<PropsWithChildren<TextSpanProps>> = props => {
	return (
		<span
			style={cssVariableForColor(props.color, "--typographic-element-color-prop-value")}
			className={mergeClassNames(
				props.style && textStylesToClassNameMapping[props.style],
				props.color && styles.useVariableColor,
			)}
			data-testid={props.testId}
		>
			{props.children}
		</span>
	);
};

export type HeadingProps = {
	children?: ReactNode;
	badgeProps?: Pick<BadgeProps, "color" | "name">;
	testId?: string;
} & MarginProps &
	TextBlockAlignProps &
	TextStyleProps;

export const Heading: FC<PropsWithChildren<HeadingProps>> = ({
	style,
	color,
	children,
	badgeProps,
	marginAway,
	textCenter,
	testId,
}): React.JSX.Element => {
	const headingLevel = TypoGraphicElementInternals.useHeadingLevelContext();

	if (headingLevel === 0) {
		throw new RangeError(
			"<Heading> needs to have a <SG section>, <Section>, <Article>, or <HeadingLevel> as its ancestor.",
		);
	}

	const elementType = headingLevel > 6 ? "h6" : "h" + headingLevel;

	return (
		<span className={mergeClassNames(styles.headingWrapper, textCenter && styles.centerHeading)}>
			{createElement(
				elementType,
				{
					className: mergeClassNames(
						styles.headingHElement,
						styles.whiteSpacePreLine,
						textStylesToClassNameMapping[style || "heading-default"],
						marginAway && styles.useVariableMarginBottom,
						color && styles.useVariableColor,
					),
					style: {
						...cssVariableForColor(color, "--typographic-element-color-prop-value"),
						...cssVariableForMargin(marginAway, "--typographic-element-margin-away-value"),
					},
					"data-testid": testId,
				},
				children,
			)}
			{badgeProps && <Badge noMargin {...badgeProps} />}
		</span>
	);
};
